import { CallOptions, ContentType } from '../types';

import { buildRequestUrl } from './fetch';

/**
 * builds FormData from given data according POST-form format
 */
function buildFormData(formData: FormData, data: unknown, parentKey?: string) {
    if (
        data &&
        typeof data === 'object' &&
        !(data instanceof Date) &&
        !(data instanceof File) &&
        !(data instanceof Blob)
    ) {
        // simple object, iterate over key-value pairs
        const object = data as Record<string, unknown>;
        Object.entries(object).forEach(([key, value]) => {
            buildFormData(
                formData,
                value,
                parentKey ? `${parentKey}[${key}]` : key
            );
        });
    } else if (Array.isArray(data)) {
        // array of data -> iterate over elements
        const array = data as unknown[];
        array.forEach((value, index) => {
            buildFormData(
                formData,
                value,
                parentKey ? `${parentKey}[${index}]` : String(index)
            );
        });
    } else {
        // simple value -> convert to String and simply append
        const value = data !== null ? String(data) : '';

        formData.append(parentKey ?? '', value);
    }
}

function buildUrlEncodedBody<RequestType extends Record<string, unknown>>(
    requestData: Partial<RequestType>
) {
    const formData = new FormData();
    buildFormData(formData, requestData);

    return new URLSearchParams(formData as unknown as string[][]);
}

export const buildGetRequest = <RequestType, UrlParams>({
    url,
    requestOptions,
    queryParams,
    urlParams,
}: CallOptions<RequestType, UrlParams>) =>
    new Request(buildRequestUrl(url, queryParams, urlParams), {
        ...requestOptions,
        method: 'GET',
        body: null,
        headers: {
            Accept: ContentType.Json,
            ...requestOptions?.headers,
        },
    });

export const buildPostRequest = <RequestType, UrlParams>({
    url,
    requestOptions,
    queryParams,
    requestData,
    urlParams,
}: CallOptions<RequestType, UrlParams>) =>
    new Request(buildRequestUrl(url, queryParams, urlParams), {
        ...requestOptions,
        method: 'POST',
        body: requestData ? buildUrlEncodedBody(requestData) : null,
        headers: {
            Accept: ContentType.Json,
            ...requestOptions?.headers,
            'Content-Type': 'application/x-www-form-urlencoded',
        },
    });

export const parseJsonResponse = async <ResponseType>(
    response: Response
): Promise<ResponseType> => await response.json();
